import React from 'react';
import PropTypes from 'prop-types';
import {
    Alert,
    Box,
    Button,
    Chip,
    Divider,
    Paper,
    Stack,
    Typography,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ShoppingBasketOutlinedIcon from '@mui/icons-material/ShoppingBasketOutlined';
import {
    CountryChipLabel,
    getCountryChipDisplay,
} from '../../shared/securityDisplayPrimitives';
import {
    addDialogIconBadgeSx,
    addDialogSoftCardSx,
} from '../WatchlistAddSecurityPrimitives';
import { MAX_PICKS_PER_SAVE, identifierTypeLabel } from './pendingAdditions';

// Where a pick came from: a search result, or the identifier it was looked up by.
function sourceLabel(pick) {
    return pick.identifierType
        ? `${identifierTypeLabel(pick.identifierType)} ${pick.identifierValue}`
        : 'Search';
}

/**
 * The basket both tabs of Build Watchlist additions fill (picks from
 * pendingAdditions.js). `capacity` is what the last search reported, if any.
 */
export default function AdditionsBasket({
    picks,
    capacity = null,
    busy,
    onRemove,
}) {
    const basketLimit = capacity
        ? Math.max(0, Math.min(
            MAX_PICKS_PER_SAVE,
            capacity.max_securities - capacity.current_security_count,
        ))
        : null;
    return (
        <Paper variant="outlined" sx={addDialogSoftCardSx}>
            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={{ xs: 1.5, sm: 2.5 }}
                alignItems={{ xs: 'flex-start', sm: 'center' }}
            >
                <Stack direction="row" spacing={2} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
                    <Box aria-hidden="true" sx={addDialogIconBadgeSx}>
                        <ShoppingBasketOutlinedIcon />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
                            Additions basket ({picks.length})
                        </Typography>
                        {picks.length === 0 && (
                            <Typography variant="body2" color="text.secondary">
                                Add securities from either tab. A security found both ways
                                appears once.
                            </Typography>
                        )}
                    </Box>
                </Stack>
                <Divider
                    orientation="vertical"
                    flexItem
                    sx={{ display: { xs: 'none', sm: 'block' } }}
                />
                <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ color: 'text.secondary', flexShrink: 0 }}
                >
                    <InfoOutlinedIcon fontSize="small" />
                    <Typography variant="body2" color="inherit">
                        Saved only while this dialog is open.
                    </Typography>
                </Stack>
            </Stack>
            {capacity && (
                <Typography variant="caption" component="p" color="text.secondary" sx={{ mt: 1.5 }}>
                    {capacity.current_security_count} of {capacity.max_securities} Watchlist
                    spaces are currently in use. This basket can contain up to {basketLimit} additions.
                </Typography>
            )}
            {picks.length > 0 && (
                <Stack spacing={1} sx={{ mt: 2 }}>
                    {picks.map((pick) => {
                        const title = pick.ticker || pick.label;
                        const source = sourceLabel(pick);
                        return (
                            <Paper
                                key={pick.key}
                                role="group"
                                aria-label={`Basket item ${pick.label} ${pick.country}`.trim()}
                                variant="outlined"
                                sx={{
                                    px: 1.5,
                                    py: 1,
                                    borderRadius: '10px',
                                    borderColor: pick.stale ? 'warning.main' : undefined,
                                }}
                            >
                                <Stack spacing={1}>
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography
                                                variant="subtitle2"
                                                component="div"
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    flexWrap: 'wrap',
                                                    gap: 0.75,
                                                    fontWeight: 700,
                                                }}
                                            >
                                                {title}
                                                <Box
                                                    component="span"
                                                    sx={{ color: 'text.secondary', fontWeight: 600 }}
                                                >
                                                    {pick.country
                                                        ? <CountryChipLabel display={getCountryChipDisplay(pick.country)} />
                                                        : 'Any country'}
                                                </Box>
                                                {source !== title && (
                                                    <Chip size="small" variant="outlined" label={source} />
                                                )}
                                                {pick.kind === 'unresolved' && (
                                                    <Chip size="small" color="info" label="Looked up on Apply" />
                                                )}
                                            </Typography>
                                            {pick.companyName && (
                                                <Typography
                                                    variant="caption"
                                                    component="div"
                                                    color="text.secondary"
                                                    noWrap
                                                >
                                                    {pick.companyName}
                                                </Typography>
                                            )}
                                        </Box>
                                        <Button
                                            size="small"
                                            color="error"
                                            disabled={busy}
                                            onClick={() => onRemove(pick.key)}
                                            aria-label={`Remove ${pick.label} ${pick.country} from the basket`.replace(/ +/g, ' ')}
                                        >
                                            Remove
                                        </Button>
                                    </Stack>
                                    {pick.stale && (
                                        <Alert severity="warning">
                                            Out of date. Remove this choice and add it again.
                                        </Alert>
                                    )}
                                </Stack>
                            </Paper>
                        );
                    })}
                </Stack>
            )}
        </Paper>
    );
}

AdditionsBasket.propTypes = {
    picks: PropTypes.arrayOf(PropTypes.shape({
        key: PropTypes.string.isRequired,
        kind: PropTypes.oneOf(['listing', 'unresolved']).isRequired,
        label: PropTypes.string.isRequired,
        ticker: PropTypes.string,
        companyName: PropTypes.string,
        country: PropTypes.string,
        identifierType: PropTypes.string,
        identifierValue: PropTypes.string,
        stale: PropTypes.bool,
    })).isRequired,
    capacity: PropTypes.shape({
        max_securities: PropTypes.number.isRequired,
        current_security_count: PropTypes.number.isRequired,
    }),
    busy: PropTypes.bool.isRequired,
    onRemove: PropTypes.func.isRequired,
};
